/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useEffect, useState } from "react";
import axiosWrapper from "../../services/axiosWrapper";
import { getBackendUrl } from "../../utils";
import { untranslated, useTranslation } from "@i18n";
import type { GetAllUsersInSpaceResponse, IncludedUsersOfProjectResponse } from "@ServerTypes/accounts/accounts.schema";
import type { UpdateModuleResourcePermissionResponse } from "@ServerTypes/permissions/permissions.schema";
import _ from "lodash";
import TextFieldComponent from "../../cubeComponent/Mui_Form/TextFieldComponent";
import UserNameTagComponent from "../../cubeComponent/html/UserNameTagComponent";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { BUTTON_TYPES } from "../../cubeComponent/constants/globalVariable";
import IconComponent from "../../cubeComponent/icons";
import { getRoleIdNameMapper, setModulePermissions } from "./constants";
import { folderRoles } from "@Helpers/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import Text from "@CubeComponents/html/Text/Text";

/**
 * One row of a module resource's embedded access roster, shared by all ten modules this component
 * serves. `role` and `permissions` are the numeric `folderRoles()` / `folderPermissions()` ids —
 * which is what every resource actually stores and what the server's Zod schemas declare (assets'
 * `assetAccessUserSchema` types `role` as `z.number()`).
 *
 * It was previously declared `role: string`, which is why nearly every caller reached this component
 * through an `as unknown as ComponentProps<…>["users"]` double cast: the cast was hiding this
 * mismatch rather than any problem at the call site.
 */
export type ModuleAccessUser = {
  id: string;
  role: number;
  permissions: Array<number>;
};

/** A user row from getAllUsersInSpace / includedUsersController, as this list uses it. */
type AccessUser = { id: string; fullName?: string };

/** One selectable role, exactly as `setModulePermissions` builds it. */
type RoleOption = ReturnType<typeof setModulePermissions>[number];

/**
 * The role chip shown next to each user. It carries a WHOLE option, not a partial of one: the
 * dropdown reads the chip's label off this object.
 */
type AccessUserRow = AccessUser & { value: RoleOption };

type SpaceUsersAccessProps = {
  spaceId?: string;
  users: ModuleAccessUser[];
  projectId?: string;
  resourceId: string;
  module:
    | "CLASH_TEST"
    | "FORMS"
    | "TASKS"
    | "ISSUES"
    | "REVIEWS"
    | "MAILBOX"
    | "ASSETS"
    | "CONTRACTS"
    | "ESTIMATES"
    | "SCHEDULES";
  accessChangeCompleteCallback?: () => void;
  /**
   * Renders the roster read-only. The server is the authority (it refuses the write without
   * RESOURCE_CHANGE_PERMISSION on the resource); this only keeps the caller from being offered a
   * change that will be rejected. Defaults to editable so existing callers are unaffected.
   */
  disabled?: boolean;
};
const ModuleUsersAccess = ({
  spaceId,
  users = [],
  module,
  resourceId,
  projectId,
  accessChangeCompleteCallback,
  disabled = false,
}: SpaceUsersAccessProps) => {
  const { t } = useTranslation();
  const [allUsers, setAllUsers] = useState<AccessUserRow[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<AccessUserRow[]>([]);
  const [permissionsArray, setPermissionsArray] = useState<RoleOption[]>([]);
  const dispatch = useDispatch();

  const folderRolesData = folderRoles();

  const mapUserWithPermission = (usersList: Array<AccessUser>) => {
    const roleIdNameMapper = getRoleIdNameMapper(module);
    const roleOptions = setModulePermissions(module);
    // A role id this module's dropdown filters out can still be stored on a resource, so those fall
    // back to an option carrying the same label the mapper gives them rather than an empty chip.
    const toRoleOption = (roleId: number): RoleOption =>
      roleOptions.find((role: RoleOption) => role.id === roleId) ?? {
        id: roleId,
        code: "",
        description: undefined,
        frontendName: roleIdNameMapper[roleId],
        name: "",
      };
    const finalUsers: AccessUserRow[] = usersList.map((user: AccessUser) => {
      const userRole = users.find((u: ModuleAccessUser) => u.id == user.id);
      return {
        ...user,
        value: toRoleOption(userRole ? userRole.role : folderRolesData.RESOURCE_NO_ACCESS.id),
      };
    });
    setAllUsers(finalUsers);
    setFilteredUsers(finalUsers);
  };

  const fetchAllSpaceUsers = async () => {
    const response = await axiosWrapper.post<GetAllUsersInSpaceResponse>(
      `${getBackendUrl()}/accounts/getAllUsersInSpace`,
      {
        spaceId,
      },
    );
    if (response.data.status) {
      mapUserWithPermission(response.data.result ?? []);
    }
  };

  const fetchAllProjectUsers = async () => {
    const query = { value: { projectId, source: "projectSetting" } };
    const response = await axiosWrapper.post<IncludedUsersOfProjectResponse>(
      `${getBackendUrl()}/accounts/includedUsersController`,
      query,
    );
    if (response.data.status) {
      mapUserWithPermission((response.data.result ?? []) as AccessUser[]);
    }
  };

  useEffect(() => {
    if (spaceId) {
      fetchAllSpaceUsers();
    } else if (projectId) {
      fetchAllProjectUsers();
    }
  }, [spaceId, projectId]);

  useEffect(() => {
    const res = setModulePermissions(module);
    setPermissionsArray(res);
  }, [module]);

  const handleSearchUsers: React.ComponentProps<typeof TextFieldComponent>["onChange"] = (args) => {
    const value = args.target.value.toLowerCase();
    setFilteredUsers(() => allUsers.filter((user: AccessUserRow) => user.fullName?.toLowerCase().includes(value)));
  };

  const additionalvalidations = ({ option, userId }: { option: RoleOption; userId: string }) => {
    if (module == "FORMS") {
      if (option.id == folderRolesData.RESOURCE_ADMIN.id || option.id == folderRolesData.RESOURCE_EDITOR.id) {
        dispatch(
          showToastNotification({
            content: t("This permission can only be updated through the corresponding form flow."),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return false;
      }
      const isUserHaveFullAccess = users.find(
        (user: ModuleAccessUser) => user.id == userId && [1, 2].includes(user.role),
      );
      if (isUserHaveFullAccess) {
        dispatch(
          showToastNotification({
            content: t("Permission for this user cannot be changed"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return false;
      }
    } else if (module === "REVIEWS") {
      const currentRole = users.find((user: ModuleAccessUser) => user.id == userId)?.role;
      if (option.id == folderRolesData.RESOURCE_ADMIN.id && currentRole !== folderRolesData.RESOURCE_EDITOR.id) {
        dispatch(
          showToastNotification({
            content: t("Only can update permission users can be promoted to Full Access."),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return false;
      }
    }
    return true;
  };
  const setUserPermissionData = async ({
    option,
    userId,
  }: {
    option: RoleOption | RoleOption[] | null;
    userId: string;
  }) => {
    // SelectDropDown types `onChange` for both of its modes; this one is single-select, and it hands
    // back `null` when the selection is cleared — there is no role to write then.
    const selectedRole = Array.isArray(option) ? option[0] : option;
    if (!selectedRole) {
      return;
    }
    const res = additionalvalidations({ option: selectedRole, userId });
    if (!res) {
      return;
    }
    const payload = {
      role: selectedRole.id,
      userId,
      resourceId,
      module,
    };
    const response = await axiosWrapper.post<UpdateModuleResourcePermissionResponse>(
      `${getBackendUrl()}/permissions/updateModuleResourcePermission`,
      payload,
    );
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("Successfully updated the permission"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      setAllUsers((prev: AccessUserRow[]) => {
        const newData = prev.map((user: AccessUserRow) => {
          if (user.id == userId) {
            user.value = selectedRole;
          }
          return user;
        });
        return newData;
      });
      accessChangeCompleteCallback?.();
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(response.data.errorMessage || SOMETHING_WENT_WRONG),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const itemTemplate = (data: RoleOption) => {
    return (
      <div className="flex gap-3 p-1 items-center">
        <div>
          <IconComponent name={data.name} width="1.5rem" height="1.5rem" />
        </div>
        <div className="flex flex-col">
          <div className="font-semibold">{data.frontendName}</div>
          <Text className="text-xs" id="qweqwe" sx={{ whiteSpace: "normal" }}>
            {data.description}
          </Text>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <TextFieldComponent onChange={handleSearchUsers} placeholder="Search User(s)" />
      </div>
      <div className="flex flex-col gap-5">
        {filteredUsers.map((user: AccessUserRow) => (
          <div key={user.id} className="flex">
            <div className="flex-1">{<UserNameTagComponent id={user.id} />} </div>
            <div className="flex-1 flex justify-end">
              <SelectDropDown
                id={`userRole_${user.id}`}
                disabled={disabled}
                renderOption={itemTemplate}
                onChange={(option) => setUserPermissionData({ option, userId: user.id })}
                value={user.value}
                buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                fields={{
                  text: "frontendName",
                  value: "id",
                }}
                options={permissionsArray}
                buttonWidth="9.375rem"
                popupAlign="right"
                popperWidth={400}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
export default ModuleUsersAccess;
